import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MaterialReactTable } from 'material-react-table';
import {
  Box,
  createTheme,
  ThemeProvider,
  useTheme,
  Select,
  MenuItem,
  Checkbox,
  FormControl,
  InputLabel,
  Button,
} from '@mui/material';
import EditTabSettings from '../../../inputs/EditTabSettingsWithDealership';
import TabSettingsEdit from './TabSettingsEdit';
import { updateTabSetting } from '../../../actions/apiDealerUpdates';
import { setEditTab } from '../../../actions/apiSetInfo';

const GroupTabSettings = (props) => {
  const dispatch = useDispatch();
  const globalTheme = useTheme();
  const all_tabs = useSelector((state) => state.settings_site.all_tabs);
  const relatedInstallations = useSelector((state) => state.settings_dealerships.related_installations);
  const custom_forms = useSelector((state) => state.settings_refresh?.custom_forms || []);

  const [rowSelection, setRowSelection] = useState({});
  const [massStatus, setMassStatus] = useState('');
  const [massLocations, setMassLocations] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [tableKey, setTableKey] = useState(0);
  const [error, setError] = useState(null);
  const [localTabs, setLocalTabs] = useState([]);

  useEffect(() => {
    setLocalTabs(all_tabs || []);
  }, [all_tabs]);

  const iconList = useMemo(
    () => [
      'far fa-paperclip',
      'far fa-clock',
      'far fa-alarm-clock',
      'far fa-cogs',
      'far fa-usd-square',
      'far fa-dollar-sign',
      'far fa-camera-retro',
      'far fa-camera-alt',
      'far fa-clipboard-list',
      'far fa-car',
      'far fa-clipboard-check',
      'far fa-tasks',
      'far fa-chart-bar',
      'far fa-ellipsis-h-alt',
      'far fa-receipt',
      'far fa-calendar-plus',
      'far fa-check-circle',
    ],
    [],
  );

  const tableTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: 'light',
          primary: {
            main: '#007bff',
          },
        },
        components: {
          MuiCheckbox: {
            styleOverrides: {
              root: {
                '&.Mui-checked': {
                  color: '#007bff',
                },
                '&:hover': {
                  backgroundColor: 'rgba(0, 123, 255, 0.04)',
                },
              },
            },
          },
        },
      }),
    [globalTheme],
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: 'active',
        header: 'Status',
        enableColumnFilter: true,
        filterVariant: 'select',
        filterFn: (row, id, filterValue) => {
          if (filterValue === '') return true;
          return row.original.active[0] === (filterValue === 'true');
        },
        Filter: ({ column }) => (
          <FormControl fullWidth sx={{ minWidth: 120 }}>
            <InputLabel>{column.columnDef.header}</InputLabel>
            <Select
              value={column.getFilterValue() || ''}
              onChange={(e) => column.setFilterValue(e.target.value || undefined)}
              label={column.columnDef.header}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive</MenuItem>
            </Select>
          </FormControl>
        ),
        Cell: ({ row }) => (
          <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
            <EditTabSettings
              key={`edit-tab-${row.original.id[0]}-${row.original.active[0]}`}
              type={'switch'}
              name={row.original.name[0]}
              label={row.original.label[0]}
              checked={row.original.active[0]}
              dataid={row.original.id[0]}
              description={row.original.description[0]}
            />
          </Box>
        ),
      },
      {
        accessorKey: 'icon',
        header: 'Icon',
        enableEditing: true,
        editVariant: 'select',
        muiEditTextFieldProps: ({ row, table }) => ({
          select: true,
          value: row.original.icon[0] || '',
          onChange: async (event) => {
            const iconValue = event.target.value;
            const id = row.original.id[0];
            setLocalTabs((prev) =>
              prev.map((tab) =>
                tab.id === id ? { ...tab, icon: iconValue } : tab,
              ),
            );
            try {
              await dispatch(updateTabSetting(id, { icon: iconValue }, 'editTabPropertyError'));
              setError(null);
              setTableKey((prev) => prev + 1);
              table.setEditingCell(null);
            } catch (ex) {
              setLocalTabs(all_tabs || []);
              setError('Failed to update icon. Please try again.');
              console.error('Icon update error:', ex);
            }
          },
          onClick: (event) => event.stopPropagation(),
          SelectProps: {
            renderValue: (selected) => (selected ? <i className={selected} /> : 'None'),
            MenuProps: {
              PaperProps: {
                style: { maxHeight: 300 },
              },
            },
            sx: { zIndex: 1000 },
          },
          children: [
            <MenuItem key="none" value="">
              None
            </MenuItem>,
            ...iconList.map((icon) => (
              <MenuItem key={icon} value={icon}>
                <i className={icon} style={{ marginRight: '8px' }} />
                {icon}
              </MenuItem>
            )),
          ],
        }),
        Cell: ({ row }) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: row.original.icon_bg_color[0] || 'transparent',
              color: row.original.icon_font_color[0] || 'inherit',
              padding: '8px',
            }}
          >
            {row.original.icon[0] ? <i className={row.original.icon[0]} /> : 'None'}
          </Box>
        ),
      },
      {
        accessorKey: 'locations',
        header: 'Locations',
        enableEditing: true,
        editVariant: 'select',
        filterVariant: 'select',
        filterFn: (row, id, filterValue) => {
          if (!filterValue) return true;
          const dealershipString = row.original.dealership[0] || '';
          return dealershipString.split(',').includes(filterValue);
        },
        Filter: ({ column }) => (
          <FormControl fullWidth sx={{ minWidth: 120 }}>
            <InputLabel>{column.columnDef.header}</InputLabel>
            <Select
              value={column.getFilterValue() || ''}
              onChange={(e) => column.setFilterValue(e.target.value || undefined)}
              label={column.columnDef.header}
            >
              <MenuItem value="">All</MenuItem>
              {relatedInstallations.map((option) => (
                <MenuItem key={option.id} value={option.subdomain}>
                  {option.softname || option.subdomain}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ),
        muiEditTextFieldProps: ({ row, table }) => {
          const dealershipArray =
            row.original.dealership[0] && typeof row.original.dealership[0] === 'string'
              ? row.original.dealership[0].split(',').filter(Boolean)
              : [];
          return {
            select: true,
            multiple: true,
            value: dealershipArray,
            SelectProps: {
              renderValue: (selected) => (Array.isArray(selected) ? selected.join(', ') : ''),
              MenuProps: {
                PaperProps: {
                  style: { maxHeight: 300 },
                },
              },
              sx: { zIndex: 1000 },
              multiple: true,
            },
            children: relatedInstallations.map((option) => (
              <MenuItem key={option.id} value={option.subdomain}>
                <Checkbox checked={dealershipArray.includes(option.subdomain)} />
                {option.softname || option.subdomain}
              </MenuItem>
            )),
            onChange: async (event) => {
              const selectedValues = Array.isArray(event.target.value)
                ? event.target.value
                : typeof event.target.value === 'string' && event.target.value
                ? event.target.value.split(',')
                : [];
              const id = row.original.id[0];
              setLocalTabs((prev) =>
                prev.map((tab) =>
                  tab.id === id ? { ...tab, dealership: selectedValues.join(',') } : tab,
                ),
              );
              const data = {
                dealership: selectedValues.join(','),
              };
              try {
                await dispatch(updateTabSetting(id, data, 'editTabPropertyError'));
                setError(null);
                setTableKey((prev) => prev + 1);
                table.setEditingCell(null);
              } catch (ex) {
                setLocalTabs(all_tabs || []);
                setError('Failed to update locations. Please try again.');
                console.error('Locations update error:', ex);
              }
            },
            onClick: (event) => event.stopPropagation(),
          };
        },
        Cell: ({ row }) => {
          const dealerships =
            row.original.dealership[0] && typeof row.original.dealership[0] === 'string'
              ? row.original.dealership[0].split(',').filter(Boolean)
              : [];
          const displayNames = dealerships
            .map((subdomain) => {
              const option = relatedInstallations.find((opt) => opt.subdomain === subdomain);
              return option ? option.softname || option.subdomain : subdomain;
            })
            .join(', ');
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              {displayNames || 'None'}
            </Box>
          );
        },
      },
      {
        accessorKey: 'name',
        header: 'Name',
        size: 120,
        enableEditing: false,
      },
      {
        accessorKey: 'description',
        header: 'Description',
        size: 300,
        muiTableHeadCellProps: {
          style: {
            padding: '0.75rem 0rem 0.6rem 1rem',
          },
        },
        renderCell: ({ cell }) => (
          <Box
            sx={{
              whiteSpace: 'normal',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '300px',
              width: '100%',
            }}
          >
            {cell.getValue()}
          </Box>
        ),
        enableEditing: true,
        muiEditTextFieldProps: ({ cell, row }) => ({
          onBlur: async (event) => {
            const description = event.target.value;
            const id = row.original.id[0];
            setLocalTabs((prev) =>
              prev.map((tab) =>
                tab.id === id ? { ...tab, description } : tab,
              ),
            );
            const data = {
              description,
            };
            try {
              await dispatch(updateTabSetting(id, data, 'editTabPropertyError'));
              setError(null);
              setTableKey((prev) => prev + 1);
            } catch (ex) {
              setLocalTabs(all_tabs || []);
              setError('Failed to update description. Please try again.');
              console.error('Description update error:', ex);
            }
          },
        }),
      },
      {
        accessorKey: 'form',
        header: 'Form',
        size: 300,
        muiTableHeadCellProps: {
          style: {
            padding: '0.75rem 0rem 0.6rem 1rem',
          },
        },
        renderCell: ({ cell }) => (
          <Box
            sx={{
              whiteSpace: 'normal',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '300px',
              width: '100%',
            }}
          >
            {cell.getValue() || 'None'}
          </Box>
        ),
        enableEditing: true,
        editVariant: 'select',
        muiEditTextFieldProps: ({ row, table }) => ({
          select: true,
          value: row.original.form[0] || '',
          onChange: async (event) => {
            const form = event.target.value;
            const id = row.original.id[0];
            setLocalTabs((prev) =>
              prev.map((tab) =>
                tab.id === id ? { ...tab, form } : tab,
              ),
            );
            const data = {
              form,
            };
            try {
              await dispatch(updateTabSetting(id, data, 'editTabPropertyError'));
              setError(null);
              setTableKey((prev) => prev + 1);
              table.setEditingCell(null);
            } catch (ex) {
              setLocalTabs(all_tabs || []);
              setError('Failed to update form. Please try again.');
              console.error('Form update error:', ex);
            }
          },
          onClick: (event) => event.stopPropagation(),
          SelectProps: {
            renderValue: (selected) => (selected ? selected : 'None'),
            MenuProps: {
              PaperProps: {
                style: { maxHeight: 300 },
              },
            },
            sx: { zIndex: 1000 },
          },
          children: [
            <MenuItem key="none" value="">
              None
            </MenuItem>,
            ...custom_forms.map((form) => (
              <MenuItem key={form.id} value={form.name}>
                {form.name}
              </MenuItem>
            )),
          ],
        }),
      },
    ],
    [relatedInstallations, dispatch, iconList, custom_forms],
  );

  const newdata = useMemo(
    () =>
      localTabs.map((tab) => ({
        name: [tab.name],
        description: [tab.description],
        active: [tab.active],
        id: [tab.id],
        label: [tab.label],
        edit_tab: [tab],
        dealership: [tab.dealership || ''],
        icon: [tab.icon || ''],
        icon_bg_color: [tab.icon_bg_color || ''],
        icon_font_color: [tab.icon_font_color || ''],
        form: [tab.form || ''],
      })),
    [localTabs],
  );

  const tableRef = useRef();

  useEffect(() => {
    if (selectedRow !== null) {
      dispatch(setEditTab(selectedRow.name[0]));
    }
  }, [selectedRow, dispatch]);

  const handleMassAssign = async () => {
    const selectedRows = Object.keys(rowSelection).map((index) => newdata[parseInt(index)]);
    if (selectedRows.length === 0) return;

    try {
      const updatePromises = selectedRows.map((row) => {
        const data = {};
        if (massStatus !== '') {
          data.active = massStatus === 'true';
        }
        if (massLocations.length > 0) {
          data.dealership = massLocations.join(',');
        }
        if (Object.keys(data).length > 0) {
          const id = row.id[0];
          setLocalTabs((prev) =>
            prev.map((tab) =>
              tab.id === id ? { ...tab, ...data } : tab,
            ),
          );
          return dispatch(updateTabSetting(id, data, 'editTabPropertyError'));
        }
        return Promise.resolve();
      });
      await Promise.all(updatePromises);
      setRowSelection({});
      setMassStatus('');
      setMassLocations([]);
      setTableKey((prev) => prev + 1);
      setError(null);
    } catch (ex) {
      setLocalTabs(all_tabs || []);
      setError('Failed to apply mass assignment. Please try again.');
      console.error('Mass assign error:', ex);
    }
  };

  const handleMassAppendLocations = async () => {
    const selectedRows = Object.keys(rowSelection).map((index) => newdata[parseInt(index)]);
    if (selectedRows.length === 0) return;

    try {
      const updatePromises = selectedRows.map((row) => {
        const data = {};
        if (massStatus !== '') {
          data.active = massStatus === 'true';
        }
        if (massLocations.length > 0) {
          const currentDealerships =
            row.dealership[0] && typeof row.dealership[0] === 'string'
              ? row.dealership[0].split(',').filter(Boolean)
              : [];
          const updatedDealerships = [...new Set([...currentDealerships, ...massLocations])];
          data.dealership = updatedDealerships.join(',');
        }
        if (Object.keys(data).length > 0) {
          const id = row.id[0];
          setLocalTabs((prev) =>
            prev.map((tab) =>
              tab.id === id ? { ...tab, dealership: data.dealership } : tab,
            ),
          );
          return dispatch(updateTabSetting(id, data, 'editTabPropertyError'));
        }
        return Promise.resolve();
      });
      await Promise.all(updatePromises);
      setRowSelection({});
      setMassStatus('');
      setMassLocations([]);
      setTableKey((prev) => prev + 1);
      setError(null);
    } catch (ex) {
      setLocalTabs(all_tabs || []);
      setError('Failed to append locations. Please try again.');
      console.error('Mass append locations error:', ex);
    }
  };

  return (
    <div className="container-fluid">
      <div className="col-md-12">
        <div className="row">
          <div className="card col-md-12">
            <div className="card-body">
              <div className="d-flex flex-column">
                {error && (
                  <div className="alert alert-danger" role="alert">
                    <i className="fa fa-exclamation-circle me-2" />
                    {error}
                  </div>
                )}
                <div className="p-2 mb-3 d-flex align-items-center gap-3">
                  <FormControl className="form-group" style={{ minWidth: 120 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={massStatus}
                      onChange={(e) => setMassStatus(e.target.value)}
                      label="Status"
                      className="form-control"
                    >
                      <MenuItem value="">Select Status</MenuItem>
                      <MenuItem value="true">Active</MenuItem>
                      <MenuItem value="false">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl className="form-group" style={{ minWidth: 200 }}>
                    <InputLabel>Locations</InputLabel>
                    <Select
                      multiple
                      value={massLocations}
                      onChange={(e) => setMassLocations(e.target.value)}
                      label="Locations"
                      className="form-control"
                      renderValue={(selected) => selected.join(', ')}
                    >
                      {relatedInstallations.map((option) => (
                        <MenuItem key={option.id} value={option.subdomain}>
                          <Checkbox checked={massLocations.includes(option.subdomain)} />
                          {option.softname || option.subdomain}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    color="primary"
                    className="btn btn-primary"
                    onClick={handleMassAssign}
                    disabled={Object.keys(rowSelection).length === 0 || (massStatus === '' && massLocations.length === 0)}
                  >
                    Override with Selected
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    className="btn btn-primary"
                    onClick={handleMassAppendLocations}
                    disabled={Object.keys(rowSelection).length === 0 || massLocations.length === 0}
                  >
                    Add to Selected
                  </Button>
                </div>
                <div className="p-2 list-group setscroll">
                  <ThemeProvider theme={tableTheme}>
                    <MaterialReactTable
                      key={tableKey}
                      tableRef={tableRef}
                      columns={columns}
                      data={newdata}
                      editDisplayMode="cell"
                      enableEditing
                      enableRowSelection
                      enableMultiRowSelection
                      enableGrouping
                      enableColumnFilters
                      state={{ rowSelection }}
                      onRowSelectionChange={(updater) => {
                        const newSelection = typeof updater === 'function' ? updater(rowSelection) : updater;
                        setRowSelection(newSelection);
                      }}
                      initialState={{
                        showColumnFilters: true,
                        pagination: {
                          pageSize: 100,
                        },
                      }}
                      renderDetailPanel={({ row }) => (
                        <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
                          <TabSettingsEdit edit_tab={row.original.name[0]} />
                        </Box>
                      )}
                      onRowExpandChange={({ row }) => setSelectedRow(row.original)}
                    />
                  </ThemeProvider>
                </div>
              </div>
            </div>
            <div className="card-footer" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupTabSettings;